import React, { useState, useCallback, useEffect } from 'react';
import { DEFAULT_GAMES, NM } from './utils/constants';
import { getPlatformState, getCostState, getDealTerms } from './utils/stateFactory';
import { useSync, loadFromSupabase } from './hooks/useSync';
import { exportToExcel } from './utils/exportExcel';
import useRole from './hooks/useRole';
import useCurrentMonth from './hooks/useCurrentMonth';
import supabase, { orgId } from './supabaseClient';
import AuthPage from './components/AuthPage';
import FinStatements from './components/PnLPanel';
import { useCashflowData } from './components/CashPanel';
import useCashSettings from './hooks/useCashSettings';
import PeoplePanel, { usePeopleMonthlyTotals } from './components/PeoplePanel';
import usePeopleCosts from './hooks/usePeopleCosts';
import useOverheadCategories from './hooks/useOverheadCategories';
import usePLUpload from './hooks/usePLUpload';
import OverheadsPanel from './components/OverheadsPanel';
import CashOverview from './components/CashOverview';
import ExecSummary from './components/ExecSummary';
import PnLOverview from './components/PnLOverview';
import GameInputsPanel from './components/GameInputsPanel';
import GameOutputPanel from './components/GameOutputPanel';
import StandingDataPanel from './components/StandingDataPanel';
import AdminPanel, { isLvlUp } from './components/AdminPanel';
import PortfolioPanel from './components/PortfolioPanel';
import CurrentMonthSelector from './components/CurrentMonthSelector';
import useFXRates from './hooks/useFXRates';
import CashPanel from './components/CashPanel';

const ALL_TABS = [
  { id: 'portfolio',  label: 'Exec Summary',   n: '01', group: 'outputs' },
  { id: 'output',     label: 'Game Output',    n: '02', group: 'outputs' },
  { id: 'pnl',        label: 'Financials', n: '03', group: 'outputs' },
  { id: 'revenue',    label: 'Game Inputs',    n: '04', group: 'inputs' },
  { id: 'people',     label: 'Overheads',       n: '05', group: 'inputs' },
  { id: 'cash',       label: 'Cash',           n: '06', group: 'inputs' },
  { id: 'standing',   label: 'Standing Data',  n: '—',  group: 'config', sep: true },
  { id: 'admin',      label: 'Admin',          n: '⚙',  group: 'config', adminOnly: true },
];

const TAB_GROUPS = [
  { key: 'outputs', label: 'Outputs' },
  { key: 'inputs',  label: 'Inputs' },
  { key: 'config',  label: 'Config' },
];

function loadLocal() {
  try { const d = localStorage.getItem('pfm'); if (d) return JSON.parse(d); } catch (_) {}
  return null;
}
function loadQ1Local() {
  try { const d = localStorage.getItem('pfm_q1'); if (d) return JSON.parse(d); } catch (_) {}
  return null;
}

const CLIENT_NAME = process.env.REACT_APP_CLIENT_NAME || 'LvlUp Partners';


function SubTabBar({ options, active, onSelect, color = 'var(--blue)' }) {
  return (
    <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--e0)', marginBottom: 18 }}>
      {options.map(o => (
        <button key={o.id} onClick={() => onSelect(o.id)}
          style={{ padding: '8px 18px', background: 'none', border: 'none', cursor: 'pointer',
            fontSize: 11, letterSpacing: '.06em', fontFamily: 'var(--sans)',
            color: active === o.id ? color : 'var(--t3)',
            borderBottom: active === o.id ? `2px solid ${color}` : '2px solid transparent',
            marginBottom: -1, transition: 'all .15s' }}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

export default function App() {
  const [session, setSession]         = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [activeTab, setActiveTab]     = useState('portfolio');
  const [games, setGames]             = useState(DEFAULT_GAMES);
  const [PS, setPS]                   = useState({});
  const [CS, setCS]                   = useState({});
  const [DT, setDT]                   = useState({});
  const [Q1, setQ1]                   = useState(null);
  const [syncStatus, setSyncStatus]   = useState('');
  const [loading, setLoading]         = useState(true);
  const [selectedEntity, setSelectedEntity] = useState('');
  const [portfolioView, setPortfolioView]   = useState('company');
  const [gameInputView, setGameInputView]   = useState('assumptions');
  const [peopleView, setPeopleView]         = useState('people');
  const [adminView, setAdminView]           = useState('admin');
  const [dashboardSection, setDashboardSection] = useState('dashboard');
  const [ragStatus, setRagStatus] = useState({});
  const [entities, setEntities]             = useState([]);
  const [devStartDates, setDevStartDates]     = useState({});
  const [milestones, setMilestones]           = useState({}); // { gameName: [{amount, month, currency}] }
  const [userTypes, setUserTypes]             = useState([]);
  const [userAssignments, setUserAssignments] = useState({});
  const [bonusVals, setBonusVals]             = useState(Array(NM).fill(''));
  const [unreconVals, setUnreconVals]         = useState(Array(NM).fill(''));

  const updateBonusVals = useCallback((next) => {
    setBonusVals(next);
    supabase.from('app_settings').upsert({ org_id: orgId, key: 'bonus_vals', value: JSON.stringify(next) }, { onConflict: 'org_id,key' });
  }, []);
  const updateUnreconVals = useCallback((next) => {
    setUnreconVals(next);
    supabase.from('app_settings').upsert({ org_id: orgId, key: 'unrecon_vals', value: JSON.stringify(next) }, { onConflict: 'org_id,key' });
  }, []);

  // Check if current user can see a sub-tab key
  const canSeeSubTab = (tabKey) => {
    if (!session) return false;
    const userId = session.user.id;
    const assignment = userAssignments[userId] || {};
    const uType = userTypes.find(t => t.id === assignment.typeId);
    // Override takes precedence
    if (assignment.tabOverrides?.[tabKey] !== undefined) return assignment.tabOverrides[tabKey];
    // Fall back to type default
    if (uType?.tabs?.[tabKey] !== undefined) return uType.tabs[tabKey];
    // Admin always sees everything
    if (role === 'admin') return true;
    return true; // default open
  };

  const sSync = useSync();
  const { role, roleLoading, canSee, canEdit } = useRole(session);
  const {
    currentMonthIdx, startDate, functionalCurrency,
    updateCurrentMonth, updateStartDate, updateFunctionalCurrency,
  } = useCurrentMonth();

  const {
    rates: fxRates, updateRate: updateFXRate,
    convert: fxConvert, convertTotal: fxConvertTotal,
    convertFrom: fxConvertFrom,
  } = useFXRates(functionalCurrency, currentMonthIdx);

  const { settings: cashSettings, save: saveCashSettings } = useCashSettings();

  const {
    employees, departments, oncostsPct, payRisePct, payRiseMonth,
    updateEmployees, updateDepartments, updateOncosts, updatePayRise,
  } = usePeopleCosts();

  const {
    categories: ohCategories, values: ohValues, loaded: ohLoaded,
    saveCategories: saveOhCategories,
    addCategory: addOhCategory, updateCategory: updateOhCategory, deleteCategory: deleteOhCategory,
    addSub: addOhSub, updateSub: updateOhSub, deleteSub: deleteOhSub,
    updateValue: updateOhValue,
    getCategoryMonthlyTotal,
    getAllMonthlyTotal: getOhAllMonthlyTotal,
  } = useOverheadCategories();

  const { devTotal: peopleDevTotal, ohTotal: peopleOhTotal } = usePeopleMonthlyTotals(
    employees, departments, oncostsPct, payRisePct, payRiseMonth,
    startDate, functionalCurrency, fxConvertFrom
  );

  const plUpload = usePLUpload();

  // Wrapped getCategoryMonthlyTotal that automatically applies actuals for locked months
  const getCategoryMonthlyTotalWithActuals = useCallback((catId) => {
    return getCategoryMonthlyTotal(catId, plUpload?.actuals, plUpload?.mappings, startDate, currentMonthIdx);
  }, [getCategoryMonthlyTotal, plUpload?.actuals, plUpload?.mappings, startDate, currentMonthIdx]);

  // Total people costs including bonus = peopleOhTotal + bonus
  const totalPeopleCosts = Array.from({ length: NM }, (_, i) =>
    (peopleOhTotal?.[i] || 0) + (parseFloat(bonusVals?.[i]) || 0)
  );
  const payrollMonthly = Array.from({ length: NM }, (_, i) => (peopleOhTotal?.[i] || 0) + (peopleDevTotal?.[i] || 0));
  const cashflowData = useCashflowData(games, PS, CS, DT, functionalCurrency, fxConvert, cashSettings, ohCategories, getCategoryMonthlyTotal, payrollMonthly);

  // ── Auth state
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setAuthLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });
    return () => subscription.unsubscribe();
  }, []);

  // ── Load data once authenticated
  useEffect(() => {
    if (!session) return;
    (async () => {
      let data = null;
      try { data = await loadFromSupabase(); } catch (_) {}
      if (!data) data = loadLocal();
      if (data) {
        setGames(data.games || DEFAULT_GAMES);
        setPS(data.PS || {});
        setCS(data.CS || {});
        setDT(data.DT || {});
      }
      setQ1(loadQ1Local());
      try {
        const { data: entData } = await supabase.from('entities').select('id, name').eq('org_id', orgId).order('name');
        setEntities(entData || []);
      } catch (_) {}
      try {
        const { data: bData } = await supabase.from('app_settings').select('value').eq('org_id', orgId).eq('key', 'bonus_vals').maybeSingle();
        if (bData?.value) try { setBonusVals(JSON.parse(bData.value)); } catch(_) {}
        const { data: uData } = await supabase.from('app_settings').select('value').eq('org_id', orgId).eq('key', 'unrecon_vals').maybeSingle();
        if (uData?.value) try { setUnreconVals(JSON.parse(uData.value)); } catch(_) {}
        const { data: utData } = await supabase.from('app_settings').select('value').eq('org_id', orgId).eq('key', 'user_types').maybeSingle();
        if (utData?.value) try { setUserTypes(JSON.parse(utData.value) || []); } catch (_) {}
        const { data: uaData } = await supabase.from('app_settings').select('value').eq('org_id', orgId).eq('key', 'user_assignments').maybeSingle();
        if (uaData?.value) try { setUserAssignments(JSON.parse(uaData.value) || {}); } catch (_) {}
      } catch (_) {}
      try {
        const { data: ragData } = await supabase.from('app_settings')
          .select('key,value').eq('org_id', orgId).like('key', 'rag_%');
        const ragMap = {};
        (ragData || []).forEach(row => {
          const game = row.key.replace('rag_', '');
          try { ragMap[game] = JSON.parse(row.value); } catch (_) {}
        });
        setRagStatus(ragMap);
      } catch (_) {}
      try {
        // Load dev start dates — also check empty org_id for rows saved before migration
        const { data: dsd } = await supabase.from('app_settings').select('key,value')
          .like('key', 'dev_start_%');
        const dsdMap = {};
        (dsd || []).forEach(row => {
          const game = row.key.replace('dev_start_', '');
          try { dsdMap[game] = JSON.parse(row.value); } catch (_) {}
        });
        setDevStartDates(dsdMap);
      } catch (_) {}
      setLoading(false);
    })();
  }, [session]);

  // ── Set active tab to first visible once role loads
  useEffect(() => {
    if (!roleLoading && role) {
      const first = ALL_TABS.find(t => {
        if (t.adminOnly) return role === 'admin';
        return role === 'admin' || canSee(t.id);
      });
      if (first) setActiveTab(first.id);
    }
  }, [roleLoading, role]); // eslint-disable-line react-hooks/exhaustive-deps

  const triggerSync = useCallback((g, p, c, d) => {
    setSyncStatus('saving');
    sSync(g, p, c, d, () => {
      setSyncStatus('saved');
      setTimeout(() => setSyncStatus(''), 2200);
    });
  }, [sSync]);

  const updatePS = useCallback((game, platKey, updater) => {
    setPS(prev => {
      const next = { ...prev, [game]: { ...(prev[game] || {}) } };
      const st = { ...(getPlatformState(next, game, platKey)) };
      updater(st);
      next[game][platKey] = st;
      triggerSync(games, next, CS, DT);
      return next;
    });
  }, [games, CS, DT, triggerSync]);

  const updateCS = useCallback((game, sec, field, monthIdx, value) => {
    setCS(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const costs = getCostState(next, game);
      costs[sec][field][monthIdx] = value;
      triggerSync(games, PS, next, DT);
      return next;
    });
  }, [games, PS, DT, triggerSync, fxConvertFrom, functionalCurrency]);

  const updateDT = useCallback((game, field, value) => {
    setDT(prev => {
      const next = { ...prev, [game]: { ...getDealTerms(prev, game), [field]: value } };
      triggerSync(games, PS, CS, next);
      return next;
    });
  }, [games, PS, CS, triggerSync]);

  const updateGames = useCallback((newGames, newPS, newCS, newDT) => {
    if (newGames !== undefined) setGames(newGames);
    if (newPS   !== undefined) setPS(newPS);
    if (newCS   !== undefined) setCS(newCS);
    if (newDT   !== undefined) setDT(newDT);
    triggerSync(newGames ?? games, newPS ?? PS, newCS ?? CS, newDT ?? DT);
  }, [games, PS, CS, DT, triggerSync]);

  const updateMilestones = useCallback(async (game, newMilestones) => {
    setMilestones(prev => ({ ...prev, [game]: newMilestones }));

    // Build updated CS from milestones and current CS state
    setCS(prevCS => {
      const next = JSON.parse(JSON.stringify(prevCS));
      if (!next[game]) next[game] = {};
      if (!next[game]['__game__']) next[game]['__game__'] = {
        recoup:    { devAdvances: Array(72).fill(''), loc: Array(72).fill(''), qa: Array(72).fill(''), otherPub: Array(72).fill(''), marketing: Array(72).fill('') },
        nonRecoup: { devAdvances: Array(72).fill(''), loc: Array(72).fill(''), qa: Array(72).fill(''), otherPub: Array(72).fill(''), marketing: Array(72).fill('') }
      };
      // Reset recoupable dev advances then repopulate from milestones
      // Convert from deal terms currency to functional currency
      next[game]['__game__'].recoup.devAdvances = Array(72).fill('');
      const dt = DT[game] || {};
      const msCurrency = dt.devAdvanceCurrency || 'USD';
      newMilestones.forEach(ms => {
        const amount = parseFloat(ms.amount);
        if (!amount || ms.month === '' || ms.month === null || ms.month === undefined) return;
        const monthIdx = parseInt(ms.month);
        if (isNaN(monthIdx)) return;
        const arrayIdx = monthIdx + 36;
        if (arrayIdx >= 0 && arrayIdx < 72) {
          // Convert to functional currency using fxConvertFrom
          const fcAmount = fxConvertFrom ? fxConvertFrom(amount, msCurrency, functionalCurrency || 'USD', Math.max(0, monthIdx)) : amount;
          const existing = parseFloat(next[game]['__game__'].recoup.devAdvances[arrayIdx]) || 0;
          next[game]['__game__'].recoup.devAdvances[arrayIdx] = String(existing + (fcAmount || amount));
        }
      });
      // Use setTimeout to trigger sync after state settles, passing current PS via closure
      setTimeout(() => triggerSync(games, PS, next, DT), 0);
      return next;
    });

    await supabase.from('app_settings').upsert(
      { org_id: orgId, key: `milestones_${game}`, value: JSON.stringify(newMilestones) },
      { onConflict: 'org_id,key' }
    );
  }, [games, PS, DT, triggerSync, fxConvertFrom, functionalCurrency]);

  const updateDevStart = useCallback(async (game, idx) => {
    setDevStartDates(prev => ({ ...prev, [game]: idx }));
    await supabase.from('app_settings').upsert(
      { org_id: orgId, key: `dev_start_${game}`, value: JSON.stringify(idx) },
      { onConflict: 'org_id,key' }
    );
  }, []);

  const saveQ1 = useCallback(() => {
    const snap = JSON.parse(JSON.stringify({ games, PS, CS, DT }));
    setQ1(snap);
    localStorage.setItem('pfm_q1', JSON.stringify(snap));
    setSyncStatus('saved');
    setTimeout(() => setSyncStatus(''), 3000);
  }, [games, PS, CS, DT]);

  const handleExport = useCallback(() => {
    exportToExcel(games, PS, CS, DT, Q1);
  }, [games, PS, CS, DT, Q1]);

  const visibleTabs = ALL_TABS.filter(t => {
    if (t.adminOnly) return role === 'admin';
    if (role === 'admin') return true;
    return canSee(t.id);
  });

  if (authLoading || (session && roleLoading)) {
    return (
      <div className="loading-screen">
        <div className="loading-inner">
          <span className="loading-dot" />
          <span className="loading-text">Loading…</span>
        </div>
      </div>
    );
  }

  if (!session) return <AuthPage />;

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-inner">
          <span className="loading-dot" />
          <span className="loading-text">Loading forecast data…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      {/* Dashboard section selector */}
      <div style={{ background: 'var(--bg)', borderBottom: '1px solid var(--e1)', padding: '0 24px', display: 'flex', alignItems: 'center', gap: 0 }}>
        {['Dashboard','Greenlight','Royalty Statements'].map(s => (
          <button key={s} onClick={() => setDashboardSection(s.toLowerCase().replace(' ','_'))}
            style={{ padding: '7px 16px', background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 10, letterSpacing: '.08em', textTransform: 'uppercase', fontFamily: 'var(--sans)',
              color: dashboardSection === s.toLowerCase().replace(' ','_') ? 'var(--blue)' : 'var(--t4)',
              borderBottom: dashboardSection === s.toLowerCase().replace(' ','_') ? '2px solid var(--blue)' : '2px solid transparent' }}>
            {s}
          </button>
        ))}
      </div>

      <header className="top">
        <div className="logo">
          <span className="logo-mark">{CLIENT_NAME}</span>
          {process.env.REACT_APP_ENV === 'dev' && (
            <span style={{
              fontSize: 9, fontWeight: 700, letterSpacing: '.12em',
              background: '#f5a623', color: '#000',
              padding: '2px 8px', borderRadius: 3,
              textTransform: 'uppercase'
            }}>DEV</span>
          )}
          <div className="logo-pipe" />
          <span className="logo-sub">Publishing Forecast</span>
        </div>
        <div className="top-right">
          {syncStatus && (
            <span className={`sync-label ${syncStatus === 'saved' ? 'on' : ''}`}>
              <span className={`sync-pip ${syncStatus === 'saved' ? 'on' : ''}`} />
              {syncStatus === 'saving' ? 'Saving…' : 'Saved'}
            </span>
          )}
          <CurrentMonthSelector
            currentMonthIdx={currentMonthIdx}
            startDate={startDate}
            onUpdate={updateCurrentMonth}
          />
          {entities.length > 0 && (
            <select value={selectedEntity} onChange={e => setSelectedEntity(e.target.value)}
              style={{ minWidth: 130, fontSize: 11, padding: '6px 24px 6px 10px' }}>
              <option value="">All Entities</option>
              {entities.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          )}
          <button className="action-btn secondary" onClick={handleExport}>
            <span>↓</span><span>Export XLSX</span>
          </button>
          {role === 'admin' && (
            <button className="action-btn primary" onClick={saveQ1}>
              ◈&nbsp;&nbsp;Save as Q1 Reforecast
            </button>
          )}
          <span className="role-badge">{role}</span>
          <button className="action-btn secondary" onClick={() => supabase.auth.signOut()}>
            Sign out
          </button>
        </div>
      </header>

      {dashboardSection !== 'dashboard' && (
        <div style={{ padding: '60px 24px', textAlign: 'center', color: 'var(--t3)', fontSize: 14 }}>
          {dashboardSection === 'greenlight' ? 'Greenlight' : 'Royalty Statements'} — coming soon
        </div>
      )}
      {dashboardSection === 'dashboard' && <nav className="tabs">
        {TAB_GROUPS.map(group => {
          const groupTabs = visibleTabs.filter(t => t.group === group.key);
          if (!groupTabs.length) return null;
          return (
            <React.Fragment key={group.key}>
              <div className="tab-group-label">{group.label}</div>
              {groupTabs.map(t => (
                <button
                  key={t.id}
                  className={`tab ${activeTab === t.id ? 'active' : ''} tab-${group.key}`}
                  onClick={() => setActiveTab(t.id)}
                >
                  <span className="tab-n">{t.n}</span>
                  {t.label}
                </button>
              ))}
              <div className="tab-sep" />
            </React.Fragment>
          );
        })}
      </nav>}

      {dashboardSection === 'dashboard' && <main className="panels">
        {activeTab === 'portfolio'  && (
          <div className="panel">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <span style={{ fontSize: 9, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--t3)' }}>View</span>
              <div className="tgl-pair">
                <button className={`tbtn yes ${portfolioView === 'company' ? 'on' : ''}`} onClick={() => setPortfolioView('company')}>Company Overview</button>
                <button className={`tbtn yes ${portfolioView === 'pnl' ? 'on' : ''}`} onClick={() => setPortfolioView('pnl')}>P&amp;L Overview</button>
                <button className={`tbtn yes ${portfolioView === 'summary' ? 'on' : ''}`} onClick={() => setPortfolioView('summary')}>Portfolio Summary</button>
                <button className={`tbtn yes ${portfolioView === 'cash' ? 'on' : ''}`} onClick={() => setPortfolioView('cash')}>Cash Overview</button>
              </div>
            </div>
            {portfolioView === 'summary' && (
              <PortfolioPanel
                games={games} PS={PS} CS={CS} DT={DT}
                currentMonthIdx={currentMonthIdx}
                functionalCurrency={functionalCurrency}
                convertTotal={fxConvertTotal}
                convert={fxConvert}
                startDate={startDate}
                ragStatus={ragStatus}
                onUpdateRagStatus={async (game, val) => {
                  setRagStatus(prev => ({ ...prev, [game]: val }));
                  await supabase.from('app_settings').upsert(
                    { org_id: orgId, key: `rag_${game}`, value: JSON.stringify(val) },
                    { onConflict: 'org_id,key' }
                  );
                }}
              />
            )}
            {portfolioView === 'company' && (
              <ExecSummary
                games={games} PS={PS} CS={CS} DT={DT}
                currentMonthIdx={currentMonthIdx}
                startDate={startDate}
                functionalCurrency={functionalCurrency}
                convert={fxConvert}
                convertTotal={fxConvertTotal}
                ragStatus={ragStatus}
              />
            )}
            {portfolioView === 'pnl' && (
              <PnLOverview
                games={games} PS={PS} CS={CS} DT={DT}
                currentMonthIdx={currentMonthIdx}
                startDate={startDate}
                functionalCurrency={functionalCurrency}
                convert={fxConvert}
                peopleOhTotal={peopleOhTotal}
                peopleDevTotal={peopleDevTotal}
                ohCategories={ohCategories}
                getCategoryMonthlyTotal={getCategoryMonthlyTotalWithActuals}
              />
            )}
            {portfolioView === 'cash' && (
              <CashOverview
                cashflowData={cashflowData}
                currentMonthIdx={currentMonthIdx}
                startDate={startDate}
                functionalCurrency={functionalCurrency}
                games={games} CS={CS} DT={DT} PS={PS}
              />
            )}
          </div>
        )}
        {activeTab === 'output' && (
          <GameOutputPanel
            games={games} PS={PS} CS={CS} DT={DT}
            startDate={startDate} currentMonthIdx={currentMonthIdx}
            functionalCurrency={functionalCurrency}
            convert={fxConvert} convertFrom={fxConvertFrom}
            defaultGame={games[0] || ''}
          />
        )}
        {activeTab === 'pnl' && (
          <FinStatements
            games={games} PS={PS} CS={CS} DT={DT}
            startDate={startDate}
            functionalCurrency={functionalCurrency}
            convert={fxConvert}
            cashflowData={cashflowData}
            peopleDevTotal={peopleDevTotal}
            peopleOhTotal={peopleOhTotal}
            ohCategories={ohCategories}
            ohValues={ohValues}
            getCategoryMonthlyTotal={getCategoryMonthlyTotalWithActuals}
            getOhAllMonthlyTotal={getOhAllMonthlyTotal}
            canSeePL={canSeeSubTab('pnl_pl')}
            canSeeCF={canSeeSubTab('pnl_cf')}
          />
        )}
        {activeTab === 'revenue' && (
          <div className="panel">
            <SubTabBar
              options={[{id:'assumptions',label:'Game Assumptions'},{id:'resource',label:'Resource Allocation'},{id:'otherincome',label:'Other Income'}]}
              active={gameInputView} onSelect={setGameInputView} color="var(--amber)" />
            {gameInputView === 'assumptions' && (
              <GameInputsPanel
                games={games} PS={PS} CS={CS} DT={DT}
                updatePS={canEdit ? updatePS : null}
                updateCS={canEdit ? updateCS : null}
                onGoToStanding={() => setActiveTab('standing')}
                readOnly={!canEdit}
                startDate={startDate}
                devStartDates={devStartDates}
                onUpdateDevStart={canEdit ? updateDevStart : null}
                functionalCurrency={functionalCurrency}
                convert={fxConvert}
                milestones={milestones}
                onUpdateMilestones={canEdit ? updateMilestones : null}
              />
            )}
            {gameInputView === 'resource' && (
              <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--t3)', fontSize: 13 }}>Resource Allocation — coming soon</div>
            )}
            {gameInputView === 'otherincome' && (
              <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--t3)', fontSize: 13 }}>Other Income — coming soon</div>
            )}
          </div>
        )}
        {activeTab === 'cash' && (
          <CashPanel
            games={games} PS={PS} CS={CS} DT={DT}
            startDate={startDate}
            functionalCurrency={functionalCurrency}
            convert={fxConvert}
            peopleOhTotal={peopleOhTotal}
            peopleDevTotal={peopleDevTotal}
            settings={cashSettings}
            save={saveCashSettings}
            ohCategories={ohCategories}
            getCategoryMonthlyTotal={getCategoryMonthlyTotalWithActuals}
          />
        )}
        {activeTab === 'standing' && (
          <StandingDataPanel
            games={games} DT={DT}
            updateGames={canEdit ? updateGames : null}
            updateDT={canEdit ? updateDT : null}
            readOnly={!canEdit}
            startDate={startDate}
            onUpdateStartDate={canEdit ? updateStartDate : null}
            functionalCurrency={functionalCurrency}
            onUpdateFunctionalCurrency={canEdit ? updateFunctionalCurrency : null}
            fxRates={fxRates}
            onUpdateFXRate={canEdit ? updateFXRate : null}
            currentMonthIdx={currentMonthIdx}
            departments={departments}
            onUpdateDepartments={canEdit ? updateDepartments : null}
            categories={ohCategories}
            onUpdateCategories={canEdit ? saveOhCategories : null}
            plUpload={plUpload}
            ohValues={ohValues}
            onRestoreOhValues={canEdit ? (vals) => {
              // Restore overhead values from snapshot
              supabase.from('app_settings').upsert(
                { org_id: orgId, key: 'overhead_values', value: JSON.stringify(vals) },
                { onConflict: 'org_id,key' }
              ).then(() => window.location.reload());
            } : null}
          />
        )}
        {activeTab === 'people' && (
          <div className="panel">
            <SubTabBar
              options={[{id:'people',label:'People'},{id:'overheads',label:'Overheads'}]}
              active={peopleView} onSelect={setPeopleView} color="var(--amber)" />
            {peopleView === 'people' && (
              <PeoplePanel
                employees={employees} departments={departments}
                oncostsPct={oncostsPct} payRisePct={payRisePct} payRiseMonth={payRiseMonth}
                updateEmployees={canEdit ? updateEmployees : null}
                updateDepartments={canEdit ? updateDepartments : null}
                updateOncosts={canEdit ? updateOncosts : null}
                updatePayRise={canEdit ? updatePayRise : null}
                startDate={startDate}
                functionalCurrency={functionalCurrency}
                convertFrom={fxConvertFrom}
                readOnly={!canEdit}
                plActuals={plUpload?.actuals}
                plMappings={plUpload?.mappings}
                currentMonthIdx={currentMonthIdx}
                bonusVals={bonusVals}
                unreconVals={unreconVals}
                onUpdateBonus={canEdit ? updateBonusVals : null}
                onUpdateUnrecon={canEdit ? updateUnreconVals : null}
              />
            )}
            {peopleView === 'overheads' && (
              <OverheadsPanel
                categories={ohCategories}
                values={ohValues}
                loaded={ohLoaded}
                addCategory={canEdit ? addOhCategory : null}
                updateCategory={canEdit ? updateOhCategory : null}
                deleteCategory={canEdit ? deleteOhCategory : null}
                addSub={canEdit ? addOhSub : null}
                updateSub={canEdit ? updateOhSub : null}
                deleteSub={canEdit ? deleteOhSub : null}
                updateValue={canEdit ? updateOhValue : null}
                getCategoryMonthlyTotal={getCategoryMonthlyTotalWithActuals}
                startDate={startDate}
                functionalCurrency={functionalCurrency}
                getActualsForMonth={plUpload?.getActualsForMonth}
                currentMonthIdx={currentMonthIdx}
                plActuals={plUpload?.actuals}
                plMappings={plUpload?.mappings}
              />
            )}
          </div>
        )}
        {activeTab === 'admin' && role === 'admin' && (
          <div className="panel">
            <SubTabBar
              options={[{id:'admin',label:'Administrators'}, ...(isLvlUp(session?.user?.email) ? [{id:'lvlup',label:'Lvl Up'}] : [])]}
              active={adminView} onSelect={setAdminView} color="var(--t2)" />
            {adminView === 'admin' && <AdminPanel session={session} activeSubTab='admin' />}
            {adminView === 'lvlup' && <AdminPanel session={session} activeSubTab='lvlup' />}
          </div>
        )}
      </main>}
    </div>
  );
}
